import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Camera, Plus, Search, Trash2, Upload, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ImageUploader } from '@/components/features/images';
import { PlayerFormDialog } from '@/components/features/players/PlayerFormDialog';
import { PlayerSelectionCard } from '@/components/features/players/PlayerSelectionCard';
import { TeamColorPicker } from '@/components/features/teams/TeamColorPicker';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { listPlayers } from '@/db/players';
import {
  deleteTeamPreset,
  getTeamPreset,
  restoreTeamPreset,
  updateTeamPreset,
} from '@/db/team-presets';
import { useFormErrorScroll } from '@/hooks/useFormErrorScroll';
import { useTeamAvatar } from '@/hooks/useImages';
import { handleBackgroundError, handleUserFacingError } from '@/lib/error-handler';
import { toastDeleteWithUndo, toastError, toastPromise, toastSuccess } from '@/lib/toast-helpers';
import { TeamPresetSchema } from '@/schemas';
import {
  parseImageId,
  parseTeamId,
  parsePlayerId,
  safeParseTeamId,
  type ImageId,
  type Player,
  type PlayerId,
} from '@/schemas';
import { useSessionStore } from '@/stores';

const EditTeamPresetSchema = TeamPresetSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  userId: true, // userId cannot be changed via edit form
  avatarImageId: true, // Omit branded type, use imageId string instead
})
  .partial()
  .extend({
    playerIds: z.array(z.string()).optional(), // Use string[] in form, convert to PlayerId[] on submit
    imageId: z.string().optional(), // Use string in form, convert to ImageId on submit
  });

type EditTeamPresetForm = z.infer<typeof EditTeamPresetSchema>;

export const Route = createFileRoute('/team-presets/$presetId/edit')({
  loader: ({ params }) => {
    const parseResult = safeParseTeamId(params.presetId);
    if (!parseResult.success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- notFound() is TanStack Router's official API for 404 handling, not a standard Error
      throw notFound();
    }
  },
  component: EditTeamPresetPage,
});

function EditTeamPresetPage() {
  const { presetId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [players, setPlayers] = useState<Player[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(true);
  const [showPlayerDialog, setShowPlayerDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPlayerId, setEditingPlayerId] = useState<PlayerId | undefined>();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const [avatarUploadOpen, setAvatarUploadOpen] = useState(false);
  const [pendingAvatarImageId, setPendingAvatarImageId] = useState<ImageId | undefined>(undefined);
  const [preset, setPreset] = useState<import('@/schemas').TeamPreset | undefined>(undefined);
  const sessionStore = useSessionStore();
  const { avatarUrl: teamAvatarUrl } = useTeamAvatar(preset?.avatarImageId ?? pendingAvatarImageId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EditTeamPresetForm>({
    resolver: zodResolver(EditTeamPresetSchema),
  });

  const watchedPlayerIds = watch('playerIds');
  const watchedColor = watch('color');
  const watchedName = watch('name');
  const watchedNickname = watch('nickname');

  const loadPlayers = useCallback(async () => {
    try {
      const playerList = await listPlayers();
      setPlayers(playerList);
    } catch (error: unknown) {
      handleBackgroundError(error, { operation: 'loadPlayers' });
    } finally {
      setIsLoadingPlayers(false);
    }
  }, []);

  const loadPreset = useCallback(async () => {
    try {
      const brandedPresetId = parseTeamId(presetId);
      const loadedPreset = await getTeamPreset(brandedPresetId);
      if (!loadedPreset) {
        setError('Team preset not found');
        setIsLoading(false);
        return;
      }

      setPreset(loadedPreset);

      reset({
        name: loadedPreset.name,
        nickname: loadedPreset.nickname,
        imageId: loadedPreset.avatarImageId ? String(loadedPreset.avatarImageId) : undefined,
        color: loadedPreset.color,
        playerIds: loadedPreset.playerIds?.map(String),
      });

      document.title = `${t('edit', 'Edit')} ${loadedPreset.name || t('teams.unnamedPreset')} - ${t('app.name', 'One Score')}`;
    } catch (error: unknown) {
      const errorMessage = handleUserFacingError(
        error,
        t('teams.loadError', 'Failed to load team preset'),
        { operation: 'loadTeamPreset', presetId },
      );
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [presetId, reset, t]);

  useEffect(() => {
    void loadPlayers();
  }, [loadPlayers]);

  useEffect(() => {
    void loadPreset();
  }, [loadPreset]);

  // Scroll to first error field when validation fails
  useFormErrorScroll(errors);

  const handleColorChange = (color: string) => {
    setValue('color', color, { shouldValidate: true });
  };

  // Filter out selected players from available list
  const availablePlayers = players.filter((p) => !watchedPlayerIds?.includes(String(p.id)));

  const filteredAvailablePlayers = availablePlayers.filter((player) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    return (
      player.name.toLowerCase().includes(query) || player.nickname?.toLowerCase().includes(query)
    );
  });

  const selectedPlayers = players.filter((p) => watchedPlayerIds?.includes(String(p.id)));

  const handlePlayerToggle = (playerId: PlayerId) => {
    const currentPlayerIds = watchedPlayerIds ?? [];
    const playerIdString = String(playerId);
    setValue('playerIds', [...currentPlayerIds, playerIdString]);
  };

  const handleRemovePlayer = (playerId: PlayerId) => {
    const currentPlayerIds = watchedPlayerIds ?? [];
    const playerIdString = String(playerId);
    setValue(
      'playerIds',
      currentPlayerIds.filter((id) => id !== playerIdString),
    );
  };

  const handleEditPlayer = (playerId: PlayerId) => {
    setEditingPlayerId(playerId);
    setShowPlayerDialog(true);
  };

  const onSubmit = async (data: EditTeamPresetForm) => {
    setIsSaving(true);
    setError(undefined);

    try {
      const brandedPresetId = parseTeamId(presetId);
      // Convert string player IDs to PlayerId if needed
      const playerIds: PlayerId[] | undefined = data.playerIds
        ? data.playerIds.map((id) => (typeof id === 'string' ? parsePlayerId(id) : id))
        : undefined;

      const avatarImageId =
        pendingAvatarImageId ?? (data.imageId ? parseImageId(data.imageId) : preset?.avatarImageId);

      const updatePromise: Promise<void> = updateTeamPreset(brandedPresetId, {
        name: data.name,
        nickname: data.nickname,
        avatarImageId,
        color: data.color,
        playerIds,
      });
      const toastMessages = {
        loading: t('teams.updating', 'Updating team preset...'),
        success: t('teams.updateSuccess', 'Team preset updated successfully'),
        error: (err: unknown) =>
          err instanceof Error
            ? err.message
            : t('teams.updateError', 'Failed to update team preset'),
      };
      await toastPromise(updatePromise, toastMessages);
      void navigate({ to: '/team-presets' });
    } catch (updateError: unknown) {
      const errorMessage = handleUserFacingError(
        updateError,
        t('teams.updateError', 'Failed to update team preset'),
        { operation: 'updateTeamPreset', presetId },
      );
      setError(errorMessage);
      toastError(errorMessage);
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteError(undefined);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    setDeleteError(undefined);

    try {
      const brandedPresetId = parseTeamId(presetId);
      await deleteTeamPreset(brandedPresetId);
      toastDeleteWithUndo(
        t('teams.deleteSuccess', 'Team preset deleted successfully'),
        async () => {
          await restoreTeamPreset(brandedPresetId);
        },
        t('undo', 'undo'),
      );
      void navigate({ to: '/team-presets' });
    } catch (error: unknown) {
      const errorMessage = handleUserFacingError(
        error,
        t('teams.deleteError', 'Failed to delete team preset'),
        { operation: 'deleteTeamPreset', presetId },
      );
      setDeleteError(errorMessage);
      toastError(errorMessage);
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-muted-foreground">{t('loading', 'Loading...')}</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl space-y-6 px-4 mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('editTeamPreset', 'Edit Team Preset')}
          </h1>
          <p className="text-muted-foreground">
            {t('teams.description', 'Edit your reusable team configuration')}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void navigate({ to: '/team-presets' })}
          disabled={isSaving || isDeleting}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back', 'Back')}
        </Button>
      </div>

      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} className="space-y-6">
        <Card className="p-6">
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('name', 'Name')}{' '}
                  <span className="text-muted-foreground">({t('optional', 'optional')})</span>
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder={t('namePlaceholder', 'Enter preset name')}
                  disabled={isSaving}
                  maxLength={100}
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                {100 - (watchedName?.length ?? 0) < 20 && (
                  <p className="text-xs text-muted-foreground">
                    {t('form.charactersRemaining', { count: 100 - (watchedName?.length ?? 0) })}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="nickname">
                  {t('nickname', 'Nickname')}{' '}
                  <span className="text-muted-foreground">({t('optional', 'optional')})</span>
                </Label>
                <Input
                  id="nickname"
                  {...register('nickname')}
                  placeholder={t('nicknamePlaceholder', 'Enter preset nickname')}
                  disabled={isSaving}
                  maxLength={50}
                  className={errors.nickname ? 'border-destructive' : ''}
                />
                {errors.nickname && (
                  <p className="text-sm text-destructive">{errors.nickname.message}</p>
                )}
                {50 - (watchedNickname?.length ?? 0) < 20 && (
                  <p className="text-xs text-muted-foreground">
                    {t('form.charactersRemaining', { count: 50 - (watchedNickname?.length ?? 0) })}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="color">{t('color', 'Color')}</Label>
                <TeamColorPicker
                  value={watchedColor}
                  onChange={handleColorChange}
                  disabled={isSaving}
                />
                {errors.color && <p className="text-sm text-destructive">{errors.color.message}</p>}
              </div>

              {/* Avatar Upload */}
              <div className="flex flex-col items-center gap-2">
                <Label className="sr-only md:not-sr-only">{t('teams.form.avatar', 'Avatar')}</Label>
                {(() => {
                  const hasAvatar = !!teamAvatarUrl;
                  let tooltipMessage: string;
                  if (isSaving) {
                    tooltipMessage = t(
                      'teams.form.avatarDisabledSubmitting',
                      'Please wait while the form is being submitted',
                    );
                  } else if (hasAvatar) {
                    tooltipMessage = t('teams.form.changeAvatar', 'Click to change avatar');
                  } else {
                    tooltipMessage = t('teams.form.uploadAvatar', 'Click to upload avatar');
                  }

                  return (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setAvatarUploadOpen(true)}
                              disabled={isSaving}
                              className="relative h-24 w-24 rounded-full border-2 p-0 transition-all hover:border-primary hover:shadow-md disabled:opacity-50"
                            >
                              {hasAvatar ? (
                                <>
                                  <img
                                    src={teamAvatarUrl}
                                    alt={t('teams.form.avatar', 'Avatar')}
                                    className="h-full w-full rounded-full object-cover"
                                  />
                                  {/* Overlay on hover */}
                                  <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity hover:opacity-100">
                                    <Upload className="h-6 w-6 text-white" />
                                  </div>
                                </>
                              ) : (
                                <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-full bg-muted">
                                  <Camera className="h-6 w-6 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">
                                    {t('teams.form.uploadAvatar', 'Upload')}
                                  </span>
                                </div>
                              )}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{tooltipMessage}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })()}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="space-y-4">
            <Label>
              {t('players', 'Players')}{' '}
              <span className="text-muted-foreground">({t('optional', 'optional')})</span>
            </Label>
            {isLoadingPlayers ? (
              <div className="flex min-h-[400px] items-center justify-center">
                <p className="text-muted-foreground">{t('loading', 'Loading...')}</p>
              </div>
            ) : (
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Available Players */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                      {t('sessions:create.players.available', 'Available Players')}
                    </h3>
                    {availablePlayers.length >= 6 && (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder={t('sessions:create.players.search', 'Search players...')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="h-8 w-48 pl-9"
                          disabled={isSaving}
                        />
                      </div>
                    )}
                  </div>

                  {availablePlayers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center space-y-4 rounded-lg border-2 border-dashed p-8">
                      <UserPlus className="h-12 w-12 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {t('sessions:create.players.noPlayers', 'No players available')}
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {filteredAvailablePlayers.map((player) => (
                        <PlayerSelectionCard
                          key={player.id}
                          player={player}
                          isSelected={false}
                          disabled={isSaving}
                          onToggle={handlePlayerToggle}
                          onEdit={handleEditPlayer}
                        />
                      ))}
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditingPlayerId(undefined);
                      setShowPlayerDialog(true);
                    }}
                    disabled={isSaving}
                    className="w-full"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('players.addPlayer')}
                  </Button>
                </div>

                {/* Selected Players */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    {t('sessions:create.players.selected', 'Selected Players')} (
                    {selectedPlayers.length})
                  </h3>

                  {selectedPlayers.length === 0 ? (
                    <div className="flex min-h-[200px] flex-col items-center justify-center space-y-2 rounded-lg border-2 border-dashed p-8">
                      <p className="text-sm text-muted-foreground">
                        {t('sessions:create.players.minimum', 'Select players to add to preset')}
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {selectedPlayers.map((player) => (
                        <PlayerSelectionCard
                          key={player.id}
                          player={player}
                          isSelected={true}
                          disabled={isSaving}
                          onToggle={handleRemovePlayer}
                          onEdit={handleEditPlayer}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {errors.playerIds && (
              <p className="text-sm text-destructive">{errors.playerIds.message}</p>
            )}
          </div>
        </Card>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={isSaving || isDeleting} className="min-w-[120px]">
            {isSaving ? t('saving', 'Saving...') : t('save', 'Save')}
          </Button>
        </div>
      </form>

      {/* Danger Zone */}
      <Card className="border-destructive/50 p-6">
        <div className="space-y-4">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-destructive">
              {t('teams.dangerZone.title', 'Danger Zone')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t(
                'teams.dangerZone.description',
                'Once you delete a team preset, this action cannot be undone.',
              )}
            </p>
          </div>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDeleteClick}
            disabled={isSaving || isDeleting}
            className="w-full sm:w-auto"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('teams.actions.delete', 'Delete Preset')}
          </Button>
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('teams.deleteConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('teams.deleteConfirm.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <div className="py-2">
              <p className="text-sm text-destructive">{deleteError}</p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteError(undefined)} disabled={isDeleting}>
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('deleting') : t('teams.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PlayerFormDialog
        open={showPlayerDialog}
        onOpenChange={(open) => {
          setShowPlayerDialog(open);
          if (!open) {
            setEditingPlayerId(undefined);
          }
        }}
        player={editingPlayerId ? players.find((p) => p.id === editingPlayerId) : undefined}
        onSuccess={(playerId) => {
          void loadPlayers();
          // Automatically add newly created player to selected players
          if (playerId) {
            const currentPlayerIds = watchedPlayerIds ?? [];
            const playerIdString = String(playerId);
            if (!currentPlayerIds.includes(playerIdString)) {
              setValue('playerIds', [...currentPlayerIds, playerIdString]);
            }
          }
        }}
      />
      <ImageUploader
        open={avatarUploadOpen}
        onOpenChange={setAvatarUploadOpen}
        entityType={preset ? 'team' : 'gallery'}
        entityId={preset ? preset.id : ''}
        isPrimary={true}
        maxFiles={1}
        onSuccess={(imageId) => {
          if (preset) {
            // Set as primary image (avatar) for existing preset
            setPendingAvatarImageId(imageId);
            void (async () => {
              try {
                const activeSession = sessionStore.activeSession;
                if (activeSession) {
                  await sessionStore.setPrimaryImage('team', preset.id, imageId);
                } else {
                  const { db } = await import('@/db/client');
                  const currentPreset = await getTeamPreset(preset.id);
                  if (currentPreset) {
                    await db.teamPresets.update(preset.id, {
                      avatarImageId: imageId,
                    });
                  }
                }
                toastSuccess(t('teams.form.avatarUploaded', 'Avatar uploaded successfully'));
                setAvatarUploadOpen(false);
                void loadPreset();
              } catch (error) {
                handleBackgroundError(error, { operation: 'setPrimaryImage', teamId: preset.id });
                toastError(
                  t('teams.form.avatarUploadError', 'Failed to set avatar. Please try again.'),
                );
                setPendingAvatarImageId(undefined);
              }
            })();
          } else {
            setPendingAvatarImageId(imageId);
            toastSuccess(t('teams.form.avatarUploaded', 'Avatar uploaded successfully'));
            setAvatarUploadOpen(false);
          }
        }}
      />
    </div>
  );
}
